import CT from '@marketplace-ai/tags';
import { useState, type ReactNode } from 'react';
import { ContractReadyNotice } from '~/features/market/contracts/ContractReadyNotice';
import { formatDate, formatWorkLocation } from '~/features/market/job-opportunities/utils';
import {
  formatResponseStatusLabel,
  formatStatusLabel,
  MarketActionRow,
  MarketContentPanel,
  MarketFactGrid,
  MarketPartySummary,
  MarketStatusPill,
  MarketTurnPill,
  type MarketAction,
  type MarketFact,
} from '~/features/shared/market-content';
import type { ServiceOpportunity } from './schemas';
import { offerRate, ServiceNegotiationHistory } from './ServiceNegotiationSummary';
import { canAcceptServiceOffer, canCounterServiceOffer } from './serviceOfferActions';

type Props = {
  opportunity: ServiceOpportunity;
  /** The status line and recovery controls; they sit directly above the actions. */
  notices: ReactNode;
  identityControls: ReactNode;
  actionsDisabled: boolean;
  viewProviderDisabled: boolean;
  negotiateDisabled: boolean;
  onViewProvider?: () => void;
  onNegotiate?: () => void;
  onAccept: (expectedNegotiationId: number) => void;
  onCounter: () => void;
  onWithdraw: () => void;
  /** [MCV-1850] The listing owner declines; the caller confirms before sending. */
  onReject: () => void;
};

const serviceFacts = (opportunity: ServiceOpportunity) => {
  const { listing, latestNegotiation: offer } = opportunity;
  const rate = offer ? offerRate(offer) : null;
  return (
    [
      {
        label: 'Status',
        value:
          opportunity.status === 'MESSAGED'
            ? 'Inquiry'
            : formatResponseStatusLabel(opportunity.status),
      },
      { label: 'Listing Type', value: formatStatusLabel(listing.listingKind) },
      { label: 'Work Location', value: formatWorkLocation(listing.workLocation) },
      rate ? { label: 'Rate', value: rate, highlight: true } : null,
      offer?.startDate || offer?.endDate
        ? {
            label: 'Duration',
            value:
              offer.startDate && offer.endDate
                ? `${formatDate(offer.startDate)} - ${formatDate(offer.endDate)}`
                : offer.startDate
                  ? `${formatDate(offer.startDate)}+`
                  : `Until ${formatDate(offer.endDate)}`,
          }
        : null,
      offer && offer.availabilitySelection.length > 0
        ? { label: 'Selected Dates', value: String(offer.availabilitySelection.length) }
        : null,
    ] as Array<MarketFact | null>
  ).filter((fact): fact is MarketFact => fact !== null);
};

/**
 * The detail column, laid out like the Job and B2B response pages for both
 * roles: header, counterparty summary, facts, notices and actions, identity
 * controls, then the shared negotiation card.
 */
export const ServiceResponseDetail = ({
  opportunity,
  notices,
  identityControls,
  actionsDisabled,
  viewProviderDisabled,
  negotiateDisabled,
  onViewProvider,
  onNegotiate,
  onAccept,
  onCounter,
  onWithdraw,
  onReject,
}: Props) => {
  const [reviewedOfferId, setReviewedOfferId] = useState<number | null>(null);
  const { listing, counterparty, latestNegotiation: offer } = opportunity;
  const responder = opportunity.viewerRole === 'RESPONDER';
  const otherParty = responder ? 'provider' : 'employer';
  const canAccept = canAcceptServiceOffer(opportunity);
  const canCounter = canCounterServiceOffer(opportunity);
  const canNegotiate =
    responder &&
    offer === null &&
    opportunity.availableActions.includes('counter') &&
    onNegotiate != null;
  const canWithdraw = opportunity.availableActions.includes('withdraw');
  const canReject = opportunity.availableActions.includes('reject');
  const acceptOffer = () => {
    const expectedNegotiationId = offer?.id;
    if (expectedNegotiationId == null || reviewedOfferId !== expectedNegotiationId) return;
    setReviewedOfferId(null);
    onAccept(expectedNegotiationId);
  };
  const actions: MarketAction[] = [
    ...(canAccept && offer
      ? [
          {
            label: 'Accept',
            cyTag: 'btn-accept-service-response',
            disabled: actionsDisabled || reviewedOfferId !== offer.id,
            onClick: acceptOffer,
            colorVariant: 'primary' as const,
          },
        ]
      : []),
    ...(canCounter
      ? [
          {
            label: 'Counter',
            cyTag: 'btn-counter-service-response',
            disabled: actionsDisabled,
            onClick: onCounter,
            colorVariant: 'secondary' as const,
          },
        ]
      : []),
    ...(canNegotiate
      ? [
          {
            label: 'Negotiate',
            cyTag: 'btn-counter-service-response',
            disabled: negotiateDisabled,
            onClick: onNegotiate,
            colorVariant: 'secondary' as const,
          },
        ]
      : []),
    ...(canReject
      ? [
          {
            label: 'Decline',
            cyTag: CT.marketResponses.serviceDeclineBtn,
            disabled: actionsDisabled,
            onClick: onReject,
            colorVariant: 'error' as const,
          },
        ]
      : []),
    ...(canWithdraw
      ? [
          {
            label: 'Withdraw',
            cyTag: 'btn-withdraw-service-response',
            disabled: actionsDisabled,
            onClick: onWithdraw,
            colorVariant: 'tertiary' as const,
          },
        ]
      : []),
  ];
  const pendingAcceptanceMessage =
    opportunity.status !== 'PENDING_ACCEPTANCE'
      ? null
      : opportunity.turn === 'YOUR_TURN'
        ? `The ${otherParty} accepted the current offer. Review and accept it to complete the agreement.`
        : opportunity.turn === 'THEIR_TURN'
          ? `You accepted. The ${otherParty} must confirm before the contract and booking are finalized.`
          : null;
  const pendingAcceptance = pendingAcceptanceMessage && (
    <div
      className="rounded-lg border border-primary-t-100 bg-primary-t-900/40 p-4 text-left"
      data-cy="service-pending-acceptance-notice"
    >
      <h3 className="text-sm font-semibold text-content-primary">Pending acceptance</h3>
      <p className="mt-1 text-sm text-content-secondary">{pendingAcceptanceMessage}</p>
    </div>
  );
  const consent = canAccept && offer && (
    <div className="space-y-3 text-left">
      <p>
        When both parties accept, permitted identities are shared for this response and its evolving
        participants, and the contract and selected availability booking are finalized. The first
        party's acceptance does not reveal identities. If the other party has already accepted this
        offer, your acceptance completes that agreement.
      </p>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={reviewedOfferId === offer.id}
          disabled={actionsDisabled}
          onChange={(event) => setReviewedOfferId(event.target.checked ? offer.id : null)}
        />
        <span>
          I reviewed all terms and selected availability for offer version {offer.version} and
          understand the acceptance consequences.
        </span>
      </label>
    </div>
  );
  const facts = serviceFacts(opportunity);

  return (
    <MarketContentPanel
      className="h-full"
      bodyClassName="flex min-h-0 flex-1 flex-col p-0"
      dataCy="service-response-detail"
      variant="scroll"
    >
      <div className="shrink-0 border-b border-grey-900 p-4 text-left sm:p-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="text-left">
            <MarketStatusPill status="responded" label="Service Response" />
            <h2 className="sub-heading-sb mt-2 text-content-primary">{listing.title}</h2>
          </div>
          <MarketTurnPill turn={opportunity.turn} cyTag="service-conversation-turn" />
        </div>
        <MarketPartySummary
          label={responder ? 'Provider' : 'Employer'}
          name={counterparty.displayName}
          description={
            responder
              ? counterparty.masked
                ? 'Identity masked'
                : 'Identity visible'
              : 'An employer is contacting you about your service.'
          }
          status={
            <MarketStatusPill
              status={counterparty.masked ? 'masked' : 'unmasked'}
              label={counterparty.masked ? 'Masked' : 'Unmasked'}
            />
          }
          action={
            responder && counterparty.providerId != null && onViewProvider ? (
              <button
                type="button"
                className="rounded-md border border-primary-50 bg-primary-50 px-3.5 py-2 text-sm font-semibold text-white hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={viewProviderDisabled}
                onClick={onViewProvider}
                data-cy="btn-service-view-provider"
              >
                View provider
              </button>
            ) : undefined
          }
        />
        <MarketFactGrid facts={facts} />
        <div className="mt-5 flex flex-col gap-5">
          {pendingAcceptance}
          {/* [MCV-1954] Both parties accepted: the contract is waiting on the Contracts page. */}
          {opportunity.status === 'ACCEPTED' && <ContractReadyNotice />}
          {notices}
          {consent}
          <MarketActionRow align="start" actions={actions} />
        </div>
      </div>
      <div className="shrink-0 p-4 text-left sm:p-6">
        {identityControls}
        <ServiceNegotiationHistory
          key={opportunity.responseId}
          opportunity={opportunity}
          reviewing={canAccept && !!offer}
        />
      </div>
    </MarketContentPanel>
  );
};
